import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { FeatureFlag, UserSettingsType, UserSettingsStateType } from './types';

// User settings stored in local storage
const UserSettingsContext = createContext<UserSettingsStateType | undefined>(undefined);

const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

interface UserSettingsProviderProps {
  children: React.ReactNode;
}

export const UserSettingsProvider = ({ children }: UserSettingsProviderProps) => {
  // Place new user settings here
  const defaultSettings: UserSettingsType = {};

  const [userSettings, setUserSettings] = useState<UserSettingsType>(() => {
    const savedSettings = localStorage.getItem('CLEOSCLIENT_user_settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  // Detect system color scheme preference
  const [prefersDark, setPrefersDark] = useState(() => window.matchMedia(PREFERS_DARK_QUERY).matches);

  useEffect(() => {
    localStorage.setItem('CLEOSCLIENT_user_settings', JSON.stringify(userSettings));
  }, [userSettings]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(PREFERS_DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  const colorMode = userSettings.colorMode ?? 'light';
  const resolvedColorMode = colorMode === 'system' ? (prefersDark ? 'dark' : 'light') : colorMode;

  useEffect(() => {
    document.documentElement.dataset.colorMode = resolvedColorMode;

    // When user wants to print the page, always do this in light mode
    // since browsers drop background colors when printing
    const onBeforePrint = () => (document.documentElement.dataset.colorMode = 'light');
    const onAfterPrint = () => (document.documentElement.dataset.colorMode = resolvedColorMode);
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [resolvedColorMode]);

  const contextValue = useMemo(() => {
    const updateUserSettings = (newSettings: UserSettingsType) => {
      setUserSettings((currentSettings) => ({ ...currentSettings, ...newSettings }));
    };

    return {
      userSettings,
      updateUserSettings,
      resolvedColorMode,
    };
  }, [userSettings, resolvedColorMode]);

  return <UserSettingsContext.Provider value={contextValue}>{children}</UserSettingsContext.Provider>;
};

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};

/** Whether the user has switched on a feature in development; off unless set. */
export const useFeatureFlag = (flag: FeatureFlag) => Boolean(useUserSettings().userSettings.featureFlags?.[flag]);
